import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import {
  CalendarDays,
  GraduationCap,
  Wallet,
  BookOpen,
  Clock3,
  Globe,
  ArrowRight,
  Download,
  DockIcon,
  FlaskConical,
  Handshake,
  Award,
  FileText,
  ClipboardCheck,
  Info,
  HeartPlus,
  BookCheck,
  Sparkles,
  ArrowUpRight,
  MapPin,
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
  Network,
  Users,
  Compass,
} from "lucide-react";

import DataFetcher from "../../hooks/DataFetcherPosgrados";
import Breadcumbs from "../../components/Breadcumbs";
import Accordion from "../../components/Accordion";
import BannerGeneral from "../../components/BannerGeneral";
import ConectUs from "../../components/ConectUs";
import Quote from "../../components/Quote";
import Modalidad from "../../components/posgrados/Modalidad";
import Duracion from "../../components/posgrados/Duracion";
import Campo from "../../components/posgrados/Campo";
import BlockRightImage from "../../components/BlockRightImage";
import PosgradoPagos from "../../components/PosgradoPagos";
import MasonryGallery from "../../components/MasonryGallery";
import LogoGallery from "../../components/LogoGallery";
import logoescuela from "../../img/iconsoferta/escuela-de-negocio.png";
const url =
  "https://be-ofertas.utpl.edu.ec/jsonapi/node/posgrado/1c765e5e-44d8-4e3e-9393-e1dc1941e9ac" +
  // 1. INCLUDES: Se define la ruta completa hacia el archivo físico de la imagen del docente
  "?include=field_imagen_portada.field_media_image," +
  "field_imagen_requisitos.field_media_image," +
  "field_imagen_perfil_de_egreso.field_media_image," +
  "field_imagen_perfil_ingreso.field_media_image," +
  "field_imagen_director.field_media_image," +
  "field_imagen1.field_media_image," +
  "field_gallery.field_media_image," +
  "field_nuestros_docentes.field_imagen_docente.field_media_image,field_brochure,field_archivo_malla_curricular" + // <-- Actualizado
  // 2. FIELDS [node--posgrado]: Campos del nodo principal (posgrado en singular)
  "&fields[node--posgrado]=title,field_titulo,field_modalidad,field_etiqueta_modalidad,field_precio,field_etiqueta_inversion," +
  "field_campo_amplio,field_duracion,field_inicio_clases,field_resolucion," +
  "field_diferencia_1,field_diferencia_2,field_diferencia_3," +
  "field_perfil_de_ingreso,field_perfil_de_ingreso_amplio,field_perfil_de_egreso,field_perfil_de_egreso_amplio,field_atributo_1,field_atributo_2,field_atributo_3," +
  "field_texto_director,field_imagen_director,field_nuestros_docentes,field_imagen1," +
  "field_imagen_portada,field_imagen_requisitos,field_requisitos_de_postulacion," +
  "field_malla_curricular,field_imagen_perfil_de_egreso,field_imagen_perfil_ingreso,field_texto_portada," +
  "field_publicado,field_enlace_postulacion,field_brochure,field_archivo_malla_curricular,field_bloque1,field_gallery" +
  // 3. FIELDS [node--docente]: Campos exactos que me indicaste
  "&fields[node--docente]=title,field_imagen_docente,field_contenido" +
  // 4. FIELDS de soporte para extraer la URI del archivo
  "&fields[media--image]=field_media_image" +
  "&fields[file--file]=uri";

export default function MaestriaMBAOnline() {
  const processContent = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");

      if (src && !src.startsWith("http")) {
        img.setAttribute(
          "src",
          `https://be-ofertas.utpl.edu.ec/${src.replace(/^\/+/, "")}`,
        );
      }
    });

    return doc.body.innerHTML;
  };

  const destinos = [
    {
      universidad: "CENTRUM PUCP",
      ciudad: "Lima, Perú",
      descripcion:
        "Un espacio académico para reflexionar sobre Emprendimiento, Innovación y Futuros 2030, amplía tu visión global, fortalece tus competencias y conecta con tendencias que están transformando el futuro de los negocios y las organizaciones.",
      badge: "Emprendimiento, innovación y futuros 2030",
      bgGradient: "from-blue-50 to-indigo-50",
    },
  ];

  const alianzas = [
    {
      tipo: "Título Europeo",
      institucion: "Universidad Politécnica de Valencia",
      siglas: "UPV",
      descripcion:
        "Impulsa tu perfil profesional en el ecosistema educativo europeo con una de las universidades politécnicas de mayor prestigio y reconocimiento en España.",
      enlace: "https://utpl.edu.ec/alianza-upv",
      colorTheme: "from-sky-400 to-indigo-500",
      accentBg: "bg-sky-500/10 text-sky-300 border-sky-400/20",
      beneficios: [
        "Prestigio y calidad del Espacio Europeo de Educación Superior",
        "Conexión con redes de investigación y desarrollo global",
        "Acceso a metodologías europeas de innovación",
      ],
    },
    {
      tipo: "Título Americano",
      institucion: "AGTU - The Global University",
      siglas: "AGTU",
      descripcion:
        "Obtén una ventaja competitiva global con una titulación bajo estándares académicos estadounidenses, diseñada para la flexibilidad y el liderazgo moderno.",
      enlace: "https://agtu.us/es/dual-degree-utpl-agtu/",
      colorTheme: "from-amber-400 to-orange-500",
      accentBg: "bg-amber-500/10 text-amber-300 border-amber-400/20",
      beneficios: [
        "Estándares académicos y enfoque del sistema de EE. UU.",
        "Flexibilidad curricular adaptada a profesionales activos",
        "Enfoque en competencias globales de alta demanda",
      ],
    },
  ];

  // Variantes para la animación de entrada de las tarjetas
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const DataDisplay = ({ data }) => {
    const infoCards = [
      {
        icon: GraduationCap,
        title: "Título",
        value: data.titulo_recibir,
      },
      {
        icon: Globe,
        title: "Modalidad",
        value: (
          <>
       
            <div
              className="[&_p]:text-slate-600 [&_p]:text-xs [&_p_strong]:text-xs 
              [&_h3]:text-slate-900
              [&_h3]:font-bold 
               [&_h3]:text-lg
              [&_h3]:m-0
               [&_h3]:p-0"
              dangerouslySetInnerHTML={{
                __html: data.etiqueta_modalidad,
              }}
            />
          </>
        ),
      },
      {
        icon: Clock3,
        title: "Duración",
        value: <Duracion value={data.duracion} />,
      },
      {
        icon: BookOpen,
        title: "Campo amplio",
        value: <Campo value={data.campo} />,
      },
      {
        icon: Wallet,
        title: "Inversión",
        value: (
          <>
            <div
              className="[&_p]:text-slate-800 [&_p]:font-bold  [&_p]:text-lg
               [&_p_strong]:text-lg
               [&_p]:p-0"
              dangerouslySetInnerHTML={{
                __html: data.etiqueta_precio,
              }}
            />
          </>
        ),
      },
      {
        icon: CalendarDays,
        title: "Inicio",
        value: data.inicio,
      },
    ];

    return (
      <div className="bg-white overflow-hidden mt-16">
        {/* HERO */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
          <img
            src={data.imagen_portada}
            alt="Portada"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-b from-[#004270]/10 to-[#004270]" />

          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <img
                className=" h-20 w-auto"
                src={logoescuela}
                alt="Escuela negocios UTPL"
              />

              <div
                className="[&_h2]:text-white [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:font-bold [&_h2]:leading-tight  [&_p]:text-xl [&_p]:text-slate-200"
                dangerouslySetInnerHTML={{
                  __html: data.field_texto_portada?.processed ?? "",
                }}
              />

              <div className="flex flex-wrap gap-4 mt-10">
                {data.field_publicado && (
                  <a
                    href={data.field_enlace_postulacion?.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-amber-400 hover:bg-blue-700 hover:text-white text-[#004270] px-8 py-4 rounded-2xl font-semibold transition-all flex items-center gap-2 shadow-2xl"
                  >
                    Postular ahora
                    <ArrowRight size={18} />
                  </a>
                )}
                {data.brochure && (
                  <a
                    href={data.brochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-2xl flex items-center gap-2 font-semibold hover:bg-white/20 transition"
                  >
                    Descargar brochure
                    <Download size={18} />
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="border-b border-slate-200 bg-white">
          <div className="mb-6">
            <Breadcumbs
              content={
                <div className="flex items-center gap-2 text-sm">
                  <NavLink to="/escuela-negocios">Escuela de Negocios</NavLink>
                  <span>/</span>
                  <NavLink to="/mba-innovacion">
                    Maestría en Administración de Empresas con mención en
                    Innovación
                  </NavLink>
                </div>
              }
            />
          </div>
        </div>

        {/* INFO CARDS DATA */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-6xl mx-auto ">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3  gap-6">
              {infoCards
                .filter((item) => item.value)
                .map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      {...fadeUp}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                      }}
                      whileHover={{
                        y: -8,
                      }}
                      className="
                bg-white
                rounded-3xl
                p-8
                shadow-sm
                border
                border-slate-200
                hover:shadow-2xl
                transition-all
              "
                    >
                      <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                        <Icon className="text-blue-600" size={28} />
                      </div>

                      <p className="text-sm text-slate-500">{item.title}</p>

                      <h3 className="font-bold text-lg text-slate-800 leading-snug">
                        {item.value}
                      </h3>
                    </motion.div>
                  );
                })}
            </div>
          </div>
        </section>

        {/* RESOLUCIÓN */}
        <section className="py-8 px-8 lg:px-0">
          <div className="max-w-6xl mx-auto ">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                  <DockIcon className="text-blue-600" size={28} />
                </div>
                <motion.div
                  {...fadeUp}
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: data.resolucion,
                  }}
                />
              </div>
              <div>
                <img
                  src={data.imagen1}
                  alt="utpl"
                  className="w-full max-w-sm object-contain rounded-lg"
                />
              </div>
            </div>

            {data.field_publicado && (
              <div className="mt-6">
                <a
                  href="#requisitos"
                  className="inline-flex items-center text-blue-600 font-medium hover:underline"
                >
                  Conoce los requisitos de postulación →
                </a>
              </div>
            )}
          </div>
        </section>

        {/* PERFIL INGRESO */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-20 items-start py-4">
            <motion.div {...fadeUp}>
              <div className="[&_p]:text-sm mb-12 [&_p]:text-slate-500">
                <Quote
                  content={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: data.info1,
                      }}
                    />
                  }
                ></Quote>{" "}
              </div>{" "}
              <div className="px-8 lg:px-0">
                <span className="text-blue-600 font-bold uppercase tracking-[0.2em]">
                  Perfil académico
                </span>
                <div
                  className="prose prose-lg max-w-none      
                [&_h2]:text-4xl
                [&_h2]:font-black
                [&_h2]:text-slate-900
                [&_h2]:leading-tight "
                  dangerouslySetInnerHTML={{
                    __html: data.ingreso,
                  }}
                />
                {data.ingreso_amplio && (
                  <div className="mt-4">
                    <Accordion
                      title="Más información"
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.ingreso_amplio,
                          }}
                        />
                      }
                    />
                  </div>
                )}{" "}
              </div>
            </motion.div>

            <motion.div {...fadeUp}>
              <img
                src={data.imagen_ingreso}
                alt="Perfil"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </section>

        {/* PERFIL EGRESO */}
        <section className="pb-24 bg-slate-50">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-20 items-start py-4">
            <motion.div {...fadeUp}>
              <img
                src={data.imagen_perfil}
                alt="Perfil"
                className="w-full h-[700px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
            <motion.div {...fadeUp}>
              <div className="[&_p]:text-sm mb-12 [&_p]:text-slate-500">
                <Quote
                  content={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: data.info2,
                      }}
                    />
                  }
                ></Quote>{" "}
              </div>
              <div className="px-8 lg:px-0">
                <span className=" text-blue-600 font-bold uppercase tracking-[0.2em]   ">
                  Perfil profesional
                </span>
                <div
                  className="prose prose-lg max-w-none  [&_h2]:text-4xl  [&_h2]:font-black  [&_h2]:text-slate-900 [&_h2]:leading-tight  "
                  dangerouslySetInnerHTML={{
                    __html: data.egreso,
                  }}
                />
                {data.egreso_amplio && (
                  <div className="mt-4">
                    <Accordion
                      title="Más información"
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.egreso_amplio,
                          }}
                        />
                      }
                    />
                  </div>
                )}{" "}
              </div>
            </motion.div>
          </div>
        </section>

        {/* DIFERENCIALES */}
        <section className="relative py-32 bg-[#004270] overflow-hidden">
          {/* DECORACIÓN */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-cyan-400/5 skew-x-12 translate-x-24" />

          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />

          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-3xl" />

          <div className="relative z-10 max-w-7xl mx-auto px-6">
            {/* HEADER */}
            <motion.div {...fadeUp} className="text-center mb-10">
              <span className=" uppercase  tracking-[0.25em] text-amber-400 font-semibold ">
                Diferenciales
              </span>

              <h2 className=" text-3xl md:text-4xl font-black text-white p-0 m-0 leading-tight ">
                ¿Qué nos diferencia?
              </h2>
            </motion.div>

            {/* CARDS */}
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Handshake,
                  content: data.diferencia1,
                },
                {
                  content: data.diferencia2,
                },
                {
                  icon: Award,
                  content: data.diferencia3,
                },
              ].map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    key={index}
                    {...fadeUp}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.15,
                    }}
                    whileHover={{
                      y: -12,
                      scale: 1.02,
                    }}
                    className="group  relative  overflow-hidden bg-white/5 backdrop-blur-xl  border  border-white/10 rounded-[2rem]  p-10 shadow-2xl hover:border-cyan-400/30 transition-all"
                  >
                    {/* Glow */}
                    <div className=" absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-blue-500/0 group-hover:from-cyan-400/10  group-hover:to-blue-500/10 transition-all duration-700 " />

                    {Icon && (
                      <div>
                        <Icon size={30} className="text-amber-400" />
                      </div>
                    )}

                    {/* CONTENIDO */}
                    <div
                      className="
    relative z-10
    [&_p]:text-slate-200
    [&_p]:text-lg
    [&_p]:leading-relaxed
    [&_strong]:text-white
    [&_ul]:space-y-3
    [&_img]:h-[70px]
    [&_img]:w-auto
    [&_img]:mx-auto
    [&_img]:block
  "
                      dangerouslySetInnerHTML={{
                        __html: processContent(item.content),
                      }}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full max-w-7xl mx-auto px-6 py-16 bg-slate-50 text-slate-800 antialiased">
          {/* Encabezado Principal */}
          <div className="mb-14 text-center md:text-left">
            <div className="inline-block bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
              Exclusivo Modalidad Presencial
            </div>
            <h2 className="text-4xl   font-bold tracking-tight text-[#004270] ">
              ¿Por qué estudiar el MBA modalidad presencial?
            </h2>
          </div>

          {/* Grid Simétrico de 3 Columnas */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            {/* Tarjeta 1: Networking y Conexión Directa */}
            <motion.div
              variants={cardVariants}
              className="flex flex-col justify-between p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 group"
            >
              <div>
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl w-fit mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
                  <GraduationCap className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors duration-200">
                  Conexión y Networking Real
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Interacción cara a cara en el aula con líderes, docentes,
                  empresarios y expertos invitados. Un entorno diseñado para
                  potenciar el debate en vivo, la cocreatividad y el trabajo
                  colaborativo de alto nivel.
                </p>
              </div>
            </motion.div>

            {/* Tarjeta 2: Salidas de Campo */}
            <motion.div
              variants={cardVariants}
              className="flex flex-col justify-between p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 group"
            >
              <div>
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl w-fit mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300">
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors duration-200">
                  Aprendizaje In Situ
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  La formación trasciende las aulas mediante visitas técnicas
                  programadas a empresas, startups y espacios de innovación
                  referentes en la ciudad de Loja, conectando la teoría con el
                  terreno real.
                </p>
              </div>
            </motion.div>

            {/* Tarjeta 3: Ecosistema de Innovación */}
            <motion.div
              variants={cardVariants}
              className="flex flex-col justify-between p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 group"
            >
              <div>
                <div className="p-3 bg-amber-50 text-amber-600 rounded-xl w-fit mb-6 group-hover:bg-amber-500 group-hover:text-white transition-colors duration-300">
                  <Network className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors duration-200">
                  Campus e Infraestructura UTPL
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Acceso físico y directo a laboratorios de vanguardia, espacios
                  de coworking tecnológico y vinculación presencial con la
                  incubadora <strong>Prendho</strong> y el{" "}
                  <strong>Parque Científico Tecnológico</strong> para acelerar
                  tus proyectos.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* MALLA */}
        <section className="py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto pt-6">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl"
            >
              {/* Decoración */}
              <div className="absolute top-0 right-0 w-72 h-72 bg-red-100 rounded-full blur-3xl opacity-30 -translate-y-20 translate-x-20" />
              <div className="relative z-10 pt-8 px-16">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600 text-white p-4 rounded-2xl shadow-lg">
                      <BookOpen size={32} />
                    </div>

                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                        Malla Curricular
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Contenido */}
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                  {/* Texto Principal / Malla */}
                  <div className="lg:col-span-6">
                    <div
                      className="
                    prose prose-lg max-w-none
                    prose-headings:text-gray-900
                    prose-p:text-gray-700
                    prose-li:text-gray-700
                    prose-strong:text-gray-900
                    mb-10
                  "
                      dangerouslySetInnerHTML={{
                        __html: data.malla,
                      }}
                    />
                  </div>

                  {/* Columna Lateral (Cards e Info Extra) */}
                  <motion.div whileHover={{ y: -4 }} className="lg:col-span-6">
                    {/* Card de Descarga */}
                    {data.malla_curricular && (
                      <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
                        <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-md mb-4">
                          <FileText className="text-red-600" size={30} />
                        </div>

                        <h3 className="text-lg font-bold text-gray-900 m-0 p-0">
                          Información académica
                        </h3>

                        <p className="text-gray-600 text-sm leading-relaxed mb-3">
                          Descarga la malla curricular completa y revisa las
                          materias, módulos y planificación académica del
                          programa.
                        </p>

                        <a
                          href={data.malla_curricular}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-center gap-3 text-red-600 font-semibold hover:text-red-700 transition"
                        >
                          Descargar documento
                          <Download
                            size={18}
                            className="group-hover:translate-y-0.5 transition-transform"
                          />
                        </a>
                      </div>
                    )}

                    {/* BLOQUE DESTACADO: Ventajas de la Modalidad Presencial */}
                    <div className="mt-6 rounded-3xl border border-red-100 bg-gradient-to-br from-white to-red-50/30 p-8 shadow-sm space-y-6">
                      <div className="inline-block bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Exclusivo Modalidad Presencial
                      </div>

                      {/* Elemento 1: Actividad Inmersiva */}
                      <div className="flex gap-4 items-start">
                        <div className="bg-red-100 p-3 rounded-xl flex-shrink-0 text-red-600">
                          <Users size={24} />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-1">
                            Actividad Inmersiva de Liderazgo
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Dentro de esta modalidad, el módulo de{" "}
                            <strong>
                              Liderazgo y Gestión del Talento Humano
                            </strong>{" "}
                            integra una experiencia inmersiva de liderazgo y
                            construcción de equipo.
                          </p>
                        </div>
                      </div>

                      <hr className="border-red-100" />

                      {/* Elemento 2: Metodología */}
                      <div className="flex gap-4 items-start">
                        <div className="bg-gray-100 p-3 rounded-xl flex-shrink-0 text-gray-700">
                          <Compass size={24} />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-1">
                            Metodología de Aprendizaje en Campus
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Predominio de clases y talleres prácticos,
                            actividades grupales, simulaciones y dinámicas
                            presenciales enriquecedoras.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Quote / Información adicional inferior */}
                    <div className="mt-10 max-w-2xl [&_p]:text-sm mb-12 [&_p]:text-slate-500">
                      {data.info3 && (
                        <Quote
                          content={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: data.info3,
                              }}
                            />
                          }
                        />
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>{" "}
            </motion.div>
          </div>
        </section>

        {/*SEMANAS INTERNACIONALES */}
        <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
          {/* Elementos decorativos de fondo sutiles */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-100/30 rounded-full blur-3xl -z-10" />

          <div className="max-w-5xl mx-auto">
            {/* Encabezado */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium mb-4"
              >
                <Globe className="w-4 h-4" />
                <span>Programas de movilidad</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl m-0 p-0"
              >
                Semana Internacional
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto"
              >
                Elige tu destino y conviértelo en una experiencia que va más
                allá del aula{" "}
                <span className="inline-block animate-bounce">🚀</span>
              </motion.p>
            </div>

            {/* Contenedor de Destinos */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {destinos.map((destino, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  className={`group relative flex flex-col justify-between p-8 rounded-2xl bg-gradient-to-br ${destino.bgGradient} border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}
                >
                  <div>
                    {/* Badge e Icono interactivo */}
                    <div className="flex justify-between items-start mb-6">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/80 backdrop-blur-sm text-xs font-semibold text-slate-700 shadow-sm border border-slate-100">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                        {destino.badge}
                      </span>

                      <div className="rounded-full bg-white p-2 text-slate-400 group-hover:text-slate-900 group-hover:rotate-45 transition-all duration-300 shadow-sm">
                        <ArrowUpRight className="w-5 h-5" />
                      </div>
                    </div>

                    {/* Títulos */}
                    <div className="mb-4">
                      <h3 className="text-3xl font-bold text-slate-900 tracking-tight">
                        {destino.universidad}
                      </h3>
                      <div className="flex items-center gap-1 mt-1 text-sm font-medium text-slate-500">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>{destino.ciudad}</span>
                      </div>
                    </div>

                    {/* Descripción */}
                    <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                      {destino.descripcion}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        {/*DOBLE TITULACION*/}
        <div className="relative min-h-screen bg-[#004270] text-slate-100 py-20 px-4 sm:px-6 lg:px-8 flex flex-col justify-center overflow-hidden font-sans">
          {/* Destellos de luz ambientales sobre el fondo #004270 */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-400/15 rounded-full blur-[120px] -z-10 pointer-events-none animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[150px] -z-10 pointer-events-none" />

          <div className="max-w-6xl mx-auto w-full relative z-10">
            {/* Encabezado Principal */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sky-200 text-sm font-medium mb-4 backdrop-blur-sm"
              >
                <Award className="w-4 h-4 text-amber-400" />
                <span>Internacionalización UTPL</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl sm:text-4xl lg:text-4xl font-bold tracking-tight text-white "
              >
                Doble Titulación Internacional
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg sm:text-xl text-sky-100/80 max-w-3xl mx-auto leading-relaxed"
              >
                Multiplica el valor de tu esfuerzo y expande tus fronteras.
                Obtén un título adicional europeo o americano gracias a nuestras
                prestigiosas alianzas globales.
              </motion.p>
            </div>

            {/* Tarjetas de Alianzas con Efecto Glassmorphism */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid gap-8 lg:grid-cols-2 lg:gap-12"
            >
              {alianzas.map((alianza, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{ y: -8 }}
                  className="group relative flex flex-col justify-between p-8 sm:p-10 rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 hover:border-white/20 hover:bg-white/[0.06] transition-all duration-300 shadow-2xl overflow-hidden"
                >
                  {/* Reflejo de luz interior */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />

                  <div>
                    {/* Cabecera de la Tarjeta */}
                    <div className="flex justify-between items-center mb-6">
                      <span
                        className={`px-3.5 py-1 rounded-full text-xs font-bold tracking-wider uppercase border ${alianza.accentBg}`}
                      >
                        {alianza.tipo}
                      </span>

                      <div className="flex items-center gap-1.5 text-xs text-sky-200/60 bg-white/5 px-3 py-1 rounded-full border border-white/5">
                        <ShieldCheck className="w-3.5 h-3.5 text-sky-300" />
                        <span>Aplican condiciones</span>
                      </div>
                    </div>

                    {/* Institución */}
                    <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2 group-hover:text-sky-100 transition-all duration-300">
                      {alianza.institucion}
                    </h2>

                    <span className="text-xs sm:text-sm font-semibold text-sky-300/80 tracking-wider">
                      Alianza Estratégica · {alianza.siglas}
                    </span>

                    {/* Descripción */}
                    <p className="mt-4 text-sky-100/70 leading-relaxed text-sm sm:text-base">
                      {alianza.descripcion}
                    </p>

                    {/* Beneficios */}
                    <div className="mt-8 space-y-3">
                      <h4 className="text-xs font-bold text-sky-200/50 uppercase tracking-widest">
                        Beneficios Destacados
                      </h4>
                      {alianza.beneficios.map((beneficio, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 text-sky-100/90 text-sm"
                        >
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                          <span>{beneficio}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Botón CTA */}
                  <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs text-sky-200/60">
                      <Globe className="w-4 h-4 text-sky-400" />
                      <span>Doble titulación certificada</span>
                    </div>

                    <a
                      href={alianza.enlace}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r ${alianza.colorTheme} text-slate-950 font-bold text-sm hover:brightness-110 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-sky-950/20`}
                    >
                      <span>Ver requisitos</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* DIRECTOR */}
        {data.imagen_director && (
          <section className="py-24 bg-white">
            <div className="max-w-6xl mx-auto  grid lg:grid-cols-2 gap-20 items-center">
              <motion.div {...fadeUp}>
                <img
                  src={data.imagen_director}
                  alt="Director"
                  className="w-auto h-[550px] object-cover rounded-3xl shadow-2xl"
                />
              </motion.div>

              <motion.div className="px-10 lg:px-0" {...fadeUp}>
                <span className="text-blue-600 font-semibold uppercase tracking-widest">
                  Directora del programa
                </span>

                <div
                  className="prose prose-lg mt-6 cta-link  "
                  dangerouslySetInnerHTML={{
                    __html: data.director,
                  }}
                />
              </motion.div>
            </div>
          </section>
        )}

        {/* DOCENTES */}
        {data.nuestros_docentes && data.nuestros_docentes.length > 0 && (
          <section className="pt-10 pb-10">
            <div className="max-w-6xl mx-auto pb-10  ">
              <motion.div {...fadeUp} className="text-center mb-10">
                <h2 className="text-4xl font-black m-0 p-0">
                  Nuestros docentes
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
                {data.nuestros_docentes.map((profe, index) => (
                  <motion.div
                    key={profe.id}
                    {...fadeUp}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                    }}
                    whileHover={{
                      y: -10,
                    }}
                    className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-2xl transition-all"
                  >
                    <img
                      src={profe.foto}
                      alt={profe.nombre}
                      className="w-full h-80 object-cover"
                    />

                    <div className="px-8">
                      <h3 className="text-lg font-bold text-slate-800">
                        {profe.nombre}
                      </h3>

                      <div
                        className=" text-slate-600 link-with-arrow-1"
                        dangerouslySetInnerHTML={{
                          __html: profe.contenido,
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
        {/*EN ALIANZA CON */}
        <section className=" text-center">
          <div
            className="pb-10"
            dangerouslySetInnerHTML={{
              __html: data.bloque1,
            }}
          />
          <LogoGallery gallery={data.galeria} />
        </section>
        {/*REQUISITOS */}
        <section
          id="requisitos"
          className="max-w-6xl mx-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="px-12">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
              <ClipboardCheck className="h-6 w-6" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Requisitos de postulación
            </h2>
            <div
              className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-p:leading-7 prose-strong:text-slate-900 prose-strong:font-semibold prose-ul:space-y-3 prose-ul:list-disc prose-ul:pl-6 prose-li:text-slate-700 prose-li:marker:text-sky-500 prose-a:text-sky-600 prose-a:font-semibold prose-a:underline prose-a:underline-offset-4 prose-a:decoration-sky-400 prose-a:transition-colors prose-a:duration-200 hover:prose-a:text-sky-800 hover:prose-a:decoration-sky-700 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-sky-400 [&_blockquote]:bg-sky-50 [&_blockquote]:px-4 [&_blockquote]:py-2 [&_blockquote]:italic"
              dangerouslySetInnerHTML={{
                __html: data.requisitos,
              }}
            />{" "}
          </div>
          {data.field_publicado && (
            <div>
              <div className="flex mt-10 px-12">
                <a
                  href={data.field_enlace_postulacion?.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-amber-400 hover:bg-blue-700 hover:text-white text-[#004270] px-8 py-4 rounded-2xl font-semibold transition-all flex items-center gap-2 shadow-2xl"
                >
                  Postular ahora
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <>
      <DataFetcher url={url}>
        {(data) => <DataDisplay data={data} />}
      </DataFetcher>
      <div className="max-w-6xl m-auto">
        <div className="mt-8 rounded-2xl border border-sky-200 bg-sky-50 p-5 flex gap-3 items-start">
          <Info className="h-5 w-5 text-sky-600 mt-1" />

          <p className="text-slate-700 text-xs leading-7 p-0">
            Implementamos adaptaciones curriculares no significativas para
            garantizar experiencias de aprendizaje inclusivas y accesibles.
          </p>
        </div>
      </div>
      <div className="mt-10 py-8">
        <ConectUs
          instagram="https://www.instagram.com/utplescueladenegocios"
          linkedin="https://www.linkedin.com/company/utplescueladenegocios"
          tiktok="https://www.tiktok.com/@utplescueladenegocios"
          facebook="https://www.facebook.com/utplescuelanegocios/"
        ></ConectUs>
      </div>

      <BannerGeneral />
    </>
  );
}
